import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, ShieldCheck, Lock, Mail, ArrowRight, Smartphone, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { EntraIcon } from "@/components/EntraIcon";
import { useAuth } from "@/context/AuthContext";
import { attemptSilentSSO, loginWithEntraRedirect, loadEntraConfig } from "@/lib/entra";

export default function Login() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/profile";
  const { user, refresh } = useAuth();

  const [stage, setStage] = useState("email"); // email | password | totp | email_otp (default to email so form shows immediately)
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [emailOtp, setEmailOtp] = useState("");
  const [methods, setMethods] = useState({ totp: false, email: false }); // available 2FA methods
  const [busy, setBusy] = useState(false);
  const [entraCfg, setEntraCfg] = useState({ enabled: false });
  const [silentAttempt, setSilentAttempt] = useState(true); // shows small inline indicator only
  const [emailSent, setEmailSent] = useState(false);

  useEffect(() => {
    if (user) navigate(next, { replace: true });
  }, [user, next, navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const cfg = await loadEntraConfig(api);
      if (cancelled) return;
      setEntraCfg(cfg);
      if (cfg.enabled) {
        const res = await attemptSilentSSO(api);
        if (cancelled) return;
        if (res.silent && res.idToken) {
          // Silent SSO succeeded: complete login server-side, then redirect.
          try {
            await api.post("/auth/entra/callback", {
              id_token: res.idToken,
              redirect_uri: `${window.location.origin}/auth/entra/callback`,
            });
            await refresh();
            if (cancelled) return;
            navigate(next, { replace: true });
            return;
          } catch (e) {
            const status = e?.response?.status;
            const detail = e?.response?.data?.detail;
            if (status === 403) {
              toast.error(detail || "Account disabled");
            }
            if (res.account?.username) setEmail(res.account.username);
          }
        } else if (res.account?.username) {
          setEmail(res.account.username);
        }
      }
      // Silent SSO complete (or Entra disabled) — hide the small inline indicator.
      setSilentAttempt(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const handleEmailContinue = async (e) => {
    e?.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/lookup", { email });
      if (data.auth_type === "entra") {
        if (!entraCfg.enabled) {
          toast.error("This account requires Entra, but Entra is not configured.");
          return;
        }
        await loginWithEntraRedirect(api);
        return;
      }
      setStage("password");
    } catch {
      toast.error("Network error");
    } finally {
      setBusy(false);
    }
  };

  const submitLogin = async (codePayload = {}) => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password, ...codePayload });
      if (data.requires_2fa) {
        setMethods({ totp: !!data.totp_enabled, email: !!data.email_otp_enabled });
        // Prefer TOTP first if available
        setStage(data.totp_enabled ? "totp" : "email_otp");
        toast.message("Enter your 2FA code");
        return;
      }
      await refresh();
      toast.success("Welcome back");
      navigate(next, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Login failed";
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e?.preventDefault();
    await submitLogin();
  };

  const handleTotpSubmit = async (e) => {
    e?.preventDefault();
    await submitLogin({ totp_code: totp });
  };

  const handleEmailOtpSubmit = async (e) => {
    e?.preventDefault();
    await submitLogin({ email_otp_code: emailOtp });
  };

  const sendEmailCode = async () => {
    setBusy(true);
    try {
      await api.post("/auth/email-otp/send", { email });
      setEmailSent(true);
      toast.success(`Code sent to ${email}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send email");
    } finally {
      setBusy(false);
    }
  };

  const switchTo = (newStage) => {
    setStage(newStage);
    setTotp(""); setEmailOtp("");
    if (newStage === "email_otp" && !emailSent) sendEmailCode();
  };

  return (
    <div className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-5 bg-zinc-950">
      <div className="col-span-1 lg:col-span-2 flex flex-col px-6 sm:px-12 py-10">
        <div className="flex items-center justify-between">
          <Logo />
        </div>

        <div className="flex-1 flex items-center">
          <div className="w-full max-w-sm">
            <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-zinc-50">Sign in</h1>
            <p className="mt-3 text-sm text-zinc-400">Access your account and connected applications.</p>

            {silentAttempt && entraCfg.enabled && (
              <div className="mt-4 flex items-center gap-2 text-xs text-zinc-600" data-testid="silent-sso-loader">
                <Loader2 className="w-3 h-3 animate-spin" />
                Checking for active Microsoft session…
              </div>
            )}

            {stage === "email" && (
              <form onSubmit={handleEmailContinue} className="mt-8 space-y-5">
                {entraCfg.enabled && (
                  <>
                    <button type="button" onClick={() => loginWithEntraRedirect(api)} className="w-full flex items-center justify-center gap-2 h-11 rounded-md bg-[#0078D4] hover:bg-[#106EBE] text-white text-sm font-medium" data-testid="entra-login-btn">
                      <EntraIcon className="w-4 h-4" /> Sign in with work Microsoft account
                    </button>
                    <div className="flex items-center gap-3 text-xs text-zinc-600">
                      <span className="h-px flex-1 bg-zinc-800" /><span className="font-mono">OR</span><span className="h-px flex-1 bg-zinc-800" />
                    </div>
                  </>
                )}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-xs uppercase tracking-wider text-zinc-500">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                    <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className="pl-9 bg-zinc-950 border-zinc-800 text-zinc-100 h-11" required autoFocus data-testid="login-email-input" />
                  </div>
                </div>
                <Button type="submit" disabled={busy || !email} className="w-full h-11 bg-white text-zinc-950 hover:bg-zinc-200" data-testid="login-continue-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue <ArrowRight className="w-4 h-4 ml-1" /></>}
                </Button>
              </form>
            )}

            {stage === "password" && (
              <form onSubmit={handlePasswordSubmit} className="mt-8 space-y-5">
                <div className="text-xs text-zinc-500 font-mono" data-testid="logging-in-as">{email}</div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-xs uppercase tracking-wider text-zinc-500">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                    <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="pl-9 bg-zinc-950 border-zinc-800 text-zinc-100 h-11" required autoFocus data-testid="login-password-input" />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button type="button" variant="ghost" onClick={() => { setStage("email"); setPassword(""); }} className="text-zinc-400 hover:text-zinc-100" data-testid="login-back-btn">Back</Button>
                  <Button type="submit" disabled={busy || !password} className="flex-1 h-11 bg-white text-zinc-950 hover:bg-zinc-200" data-testid="login-submit-btn">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
                  </Button>
                </div>
              </form>
            )}

            {stage === "totp" && (
              <form onSubmit={handleTotpSubmit} className="mt-8 space-y-5">
                <div className="flex items-center gap-2 text-sm text-zinc-300">
                  <Smartphone className="w-4 h-4 text-emerald-500" /> Authenticator app
                </div>
                <div className="space-y-2">
                  <Label htmlFor="totp" className="text-xs uppercase tracking-wider text-zinc-500">6-digit code</Label>
                  <Input id="totp" type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} value={totp} onChange={(e) => setTotp(e.target.value.replace(/\D/g, ""))} placeholder="000000" className="bg-zinc-950 border-zinc-800 text-zinc-100 h-12 text-center font-mono text-2xl tracking-[0.5em]" required autoFocus data-testid="login-totp-input" />
                </div>
                <Button type="submit" disabled={busy || totp.length !== 6} className="w-full h-11 bg-white text-zinc-950 hover:bg-zinc-200" data-testid="login-totp-submit-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify & sign in"}
                </Button>
                {methods.email && (
                  <button type="button" onClick={() => switchTo("email_otp")} className="w-full text-xs text-zinc-500 hover:text-zinc-300 transition-colors flex items-center justify-center gap-1.5" data-testid="switch-to-email-otp">
                    <Send className="w-3.5 h-3.5" /> Use email code instead
                  </button>
                )}
              </form>
            )}

            {stage === "email_otp" && (
              <form onSubmit={handleEmailOtpSubmit} className="mt-8 space-y-5">
                <div className="flex items-center gap-2 text-sm text-zinc-300">
                  <Send className="w-4 h-4 text-blue-400" /> Email verification
                </div>
                <div className="text-xs text-zinc-500">
                  {emailSent ? <>We sent a 6-digit code to <span className="font-mono text-zinc-300">{email}</span>. Enter it below.</> : "Requesting a code…"}
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-zinc-500">Code from email</Label>
                  <Input type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} value={emailOtp} onChange={(e) => setEmailOtp(e.target.value.replace(/\D/g, ""))} placeholder="000000" className="bg-zinc-950 border-zinc-800 text-zinc-100 h-12 text-center font-mono text-2xl tracking-[0.5em]" required autoFocus data-testid="login-emailotp-input" />
                </div>
                <Button type="submit" disabled={busy || emailOtp.length !== 6} className="w-full h-11 bg-white text-zinc-950 hover:bg-zinc-200" data-testid="login-emailotp-submit-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify & sign in"}
                </Button>
                <div className="flex items-center justify-between text-xs">
                  <button type="button" onClick={sendEmailCode} disabled={busy} className="text-zinc-500 hover:text-zinc-300" data-testid="resend-email-code">
                    Resend code
                  </button>
                  {methods.totp && (
                    <button type="button" onClick={() => switchTo("totp")} className="text-zinc-500 hover:text-zinc-300 flex items-center gap-1.5" data-testid="switch-to-totp">
                      <Smartphone className="w-3.5 h-3.5" /> Use authenticator app
                    </button>
                  )}
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      <div className="hidden lg:flex col-span-3 relative overflow-hidden geometric-bg">
        <div className="absolute inset-0 bg-gradient-to-br from-black/40 via-transparent to-black/80" />
        <div className="absolute inset-0 opacity-30" style={{ backgroundImage: "url('https://images.pexels.com/photos/20087316/pexels-photo-20087316.jpeg')", backgroundSize: "cover", backgroundPosition: "center", mixBlendMode: "screen" }} />
      </div>
    </div>
  );
}
